import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
type GridLayout = 'md-2' | 'lg-2' | 'lg-3';
interface CarouselProps<T> {
  items: T[];
  getKey: (item: T, index: number) => string;
  renderItem: (item: T, index: number) => React.ReactNode;
  label: string;
  tone?: 'light' | 'dark';
  /** Slide width while the rail is active. Use a width that leaves a peek of the next card. */
  itemClassName?: string;
  /** From the given breakpoint up, render as a static grid instead of a rail. */
  grid?: GridLayout;
  /** Counter text; receives the 1-based index of the first visible card. */
  formatCounter?: (position: number, total: number) => string;
}
const GRID_CLASSES: Record<GridLayout, {
  track: string;
  item: string;
  controls: string;
}> = {
  'md-2': {
    track: 'md:mx-0 md:grid md:grid-cols-2 md:gap-6 md:overflow-visible md:px-0 md:pb-0',
    item: 'md:w-auto',
    controls: 'md:hidden'
  },
  'lg-2': {
    track: 'lg:mx-0 lg:grid lg:grid-cols-2 lg:gap-6 lg:overflow-visible lg:px-0 lg:pb-0',
    item: 'lg:w-auto',
    controls: 'lg:hidden'
  },
  'lg-3': {
    track: 'lg:mx-0 lg:grid lg:grid-cols-3 lg:gap-6 lg:overflow-visible lg:px-0 lg:pb-0',
    item: 'lg:w-auto',
    controls: 'lg:hidden'
  }
};
const defaultCounter = (position: number, total: number) => `${position} / ${total}`;

/**
 * Native scroll-snap rail: touch swipe, arrow buttons (one card per press) and
 * arrow keys when the rail is focused. No autoplay.
 * Must sit inside a `px-5 sm:px-8` container — it bleeds to the screen edge.
 */
export function Carousel<T>({
  items,
  getKey,
  renderItem,
  label,
  tone = 'dark',
  itemClassName = 'w-[84%] sm:w-[46%]',
  grid,
  formatCounter = defaultCounter
}: CarouselProps<T>) {
  const trackRef = useRef<HTMLUListElement>(null);
  const [active, setActive] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const dark = tone === 'dark';
  const layout = grid ? GRID_CLASSES[grid] : null;
  const stepWidth = useCallback(() => {
    const track = trackRef.current;
    if (!track || track.children.length === 0) return 0;
    const first = track.children[0] as HTMLElement;
    const second = track.children[1] as HTMLElement | undefined;
    return second ? second.offsetLeft - first.offsetLeft : first.offsetWidth;
  }, []);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      const step = stepWidth();
      if (!step) return;
      setActive(Math.min(items.length - 1, Math.round(track.scrollLeft / step)));
      setAtStart(track.scrollLeft <= 4);
      setAtEnd(track.scrollLeft + track.clientWidth >= track.scrollWidth - 4);
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(measure);
    };
    measure();
    track.addEventListener('scroll', schedule, {
      passive: true
    });
    window.addEventListener('resize', schedule);
    return () => {
      track.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [items, stepWidth]);
  const goTo = (index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(items.length - 1, index));
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({
      left: clamped * stepWidth(),
      behavior: reduce ? 'auto' : 'smooth'
    });
  };
  const onKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'ArrowRight' && !atEnd) {
      event.preventDefault();
      goTo(active + 1);
    } else if (event.key === 'ArrowLeft' && !atStart) {
      event.preventDefault();
      goTo(active - 1);
    }
  };
  const arrowClass = `flex h-11 w-11 items-center justify-center rounded-full border transition-colors duration-150 ease-out disabled:cursor-not-allowed disabled:opacity-30 focus:outline-none focus-visible:ring-4 focus-visible:ring-arc/50 ${dark ? 'border-white/20 text-white enabled:hover:border-arc enabled:hover:text-arc-light' : 'border-ink/20 text-ink enabled:hover:border-arc enabled:hover:text-arc'}`;
  return <div data-id="element-26" data-mp="2vsgjriwzpsvm">
      <ul ref={trackRef} aria-label={`${label}. Use the arrow keys to move between cards.`} tabIndex={0} onKeyDown={onKeyDown} className={`no-scrollbar -mx-5 flex snap-x snap-mandatory scroll-px-5 gap-4 overflow-x-auto px-5 pb-2 focus:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-arc/40 sm:-mx-8 sm:scroll-px-8 sm:gap-5 sm:px-8 ${layout?.track ?? ''}`} data-id="element-27" data-mp="2vszticchlayk">
        {items.map((item, index) => <li key={getKey(item, index)} aria-label={`${index + 1} of ${items.length}`} className={`flex shrink-0 snap-start ${itemClassName} ${layout?.item ?? ''}`} data-id="element-28" data-mp="g1lowb5zq1om" data-mpi={index}>
            {renderItem(item, index)}
          </li>)}
      </ul>

      <div className={`mt-4 flex items-center justify-between gap-4 ${layout?.controls ?? ''}`} data-id="element-29" data-mp="nkbioucbjo2t">
        <div className="flex min-w-0 items-center gap-3" data-id="element-30" data-mp="shv7upak1l9j">
          <span aria-live="polite" className={`label-tech whitespace-nowrap tabular-nums ${dark ? 'text-white' : 'text-ink'}`} data-id="element-31" data-mp="xhg770tmzvms">
            {formatCounter(active + 1, items.length)}
          </span>
          <span className="flex items-center gap-1.5" aria-hidden data-id="element-32" data-mp="16hcqemx9c2ag">
            {items.map((item, index) => <span key={getKey(item, index)} className={`block h-[3px] rounded-full transition-[width,background-color] duration-200 ease-out ${index === active ? 'w-6 bg-arc' : `w-2.5 ${dark ? 'bg-white/20' : 'bg-ink/20'}`}`} data-id="element-33" data-mp="1b9o0gilpp4gq" data-mpi={index} />)}
          </span>
        </div>
        <div className="flex shrink-0 gap-2" data-id="element-34" data-mp="10hzrlz0tiej6">
          <button type="button" onClick={() => goTo(active - 1)} disabled={atStart} aria-label="Previous" className={arrowClass} data-id="element-35" data-mp="10ircofwsgtv7">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden data-id="element-36" data-mp="vhbwb15k3bj5" />
          </button>
          <button type="button" onClick={() => goTo(active + 1)} disabled={atEnd} aria-label="Next" className={arrowClass} data-id="element-37" data-mp="1aa5l5d6px40h">
            <ChevronRightIcon className="h-5 w-5" aria-hidden data-id="element-38" data-mp="1j8qz0v18vuux" />
          </button>
        </div>
      </div>
    </div>;
}